import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createMeetingApi,
  deleteMeetingApi,
  extractMeetingTasksApi,
  createDummyMeetings,
  fetchMeeting,
  fetchMeetings,
  updateMeetingApi,
  fetchMeetingTasks,
} from "./meetingsAPI";

export const getAllUserMeetings = createAsyncThunk(
  "Meetings/getAllUserMeetings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetchMeetings();
      return res;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch meetings",
      );
    }
  },
);

export const getAllMeetingTasks = createAsyncThunk(
  "Meetings/getAllMeetingTasks",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetchMeetingTasks();
      return res;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch meetings",
      );
    }
  },
);

export const createAllDummyMeetings = createAsyncThunk(
  "Meetings/getAllDummyMeetings",
  async (_, { rejectWithValue }) => {
    try {
      return await createDummyMeetings();
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch meetings",
      );
    }
  },
);

export const getMeeting = createAsyncThunk(
  "Meetings/getMeeting",
  async (meetingId, { rejectWithValue }) => {
    try {
      return await fetchMeeting(meetingId);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch meeting");
    }
  },
);

export const createMeeting = createAsyncThunk(
  "Meetings/createMeeting",
  async (body, thunkAPI) => {
    try {
      const data = await createMeetingApi(body);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteMeeting = createAsyncThunk(
  "Meetings/deleteMeeting",
  async (meetingId, thunkAPI) => {
    try {
      const data = await deleteMeetingApi(meetingId);
      return { meetingId, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);
export const updateMeeting = createAsyncThunk(
  "Meetings/updateMeeting",
  async ({ meetingId, title, participants, timezone }, thunkAPI) => {
    try {
      const { data } = await updateMeetingApi(meetingId, {
        title,
        participants,
        timezone,
      });
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const extractMeetingTasks = createAsyncThunk(
  "Meetings/extractMeetingTasks",
  async ({ meetingId }, thunkAPI) => {
    try {
      const { data } = await extractMeetingTasksApi(meetingId);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

const meetingsSlice = createSlice({
  name: "meetings",
  initialState: {
    meetings: [],
    meeting: null,
    loading: false,
    error: null,
  },
  reducers: {
    updateTaskInState: (state, action) => {
      const { meetingId, taskUuid, changes } = action.payload;
      const meeting = state.meetings.find((m) => m.uuid === meetingId);
      if (!meeting?.tasks?.actions) return;
      const idx = meeting.tasks.actions.findIndex((a) => a.uuid === taskUuid);
      if (idx !== -1) {
        meeting.tasks.actions[idx] = {
          ...meeting.tasks.actions[idx],
          ...changes,
        };
        meeting.tasks.completed = meeting.tasks.actions.filter(
          (a) => a.status === "completed"
        ).length;
        meeting.tasks.total = meeting.tasks.actions.length;
      }
    },
     removeTaskFromState: (state, action) => {
      const { meetingId, taskUuid } = action.payload;
      const meeting = state.meetings.find((m) => m.uuid === meetingId);
      if (!meeting?.tasks?.actions) return;
      meeting.tasks.actions = meeting.tasks.actions.filter(
        (a) => a.uuid !== taskUuid
      );
      meeting.tasks.total = meeting.tasks.actions.length;
      meeting.tasks.completed = meeting.tasks.actions.filter(
        (a) => a.status === "completed"
      ).length;
    },
  
  },
  extraReducers: (builder) => {
    builder
      // ---------- GET ALL MEETINGS ----------
      .addCase(getAllUserMeetings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUserMeetings.fulfilled, (state, action) => {
        state.meetings = action.payload || [];
        state.loading = false;
      })
      .addCase(getAllUserMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllMeetingTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllMeetingTasks.fulfilled, (state, action) => {
        state.meetings = action.payload || [];
        state.loading = false;
      })
      .addCase(getAllMeetingTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createAllDummyMeetings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAllDummyMeetings.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createAllDummyMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------- GET SINGLE MEETING ----------
      .addCase(getMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.meeting = null;
      })
      .addCase(getMeeting.fulfilled, (state, action) => {
        state.meeting = action.payload;
        state.loading = false;
      })
      .addCase(getMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------- CREATE MEETING ----------
      .addCase(createMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMeeting.fulfilled, (state, action) => {
        state.meetings.push(action.payload);
        state.loading = false;
      })
      .addCase(createMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---------- DELETE MEETING ----------
      .addCase(deleteMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        const { meetingId, data } = action.payload || {};
        if (Array.isArray(data)) {
          state.meetings = data;
        } else if (meetingId) {
          state.meetings = (state.meetings || []).filter(
            (n) => n?.uuid !== meetingId && n?.id !== meetingId,
          );
        }
        state.loading = false;
      })
      .addCase(deleteMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // update meeting
      .addCase(updateMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMeeting.fulfilled, (state, action) => {
        state.meeting = { ...action.payload };

        state.meetings = state.meetings.map((m) =>
          m.uuid === action.payload.uuid ? { ...action.payload } : m,
        );

        state.loading = false;
      })
      .addCase(updateMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(extractMeetingTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(extractMeetingTasks.fulfilled, (state, action) => {
        state.meeting = action.payload;
        state.loading = false;
      })
      .addCase(extractMeetingTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { updateTaskInState, removeTaskFromState } = meetingsSlice.actions;
export default meetingsSlice.reducer;
